import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Paper, Typography, useTheme, Collapse, IconButton, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { startCase } from "lodash-es";
import { useMemo } from "react";

import { DataGrid } from "../../../../components/DataGrid";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

function toTitleCase(input: string): string {
  if (input.startsWith("is")) {
    return startCase(input.slice(2)) + "?";
  }

  return startCase(input);
}

export function Extensions() {
  const { data } = useFulfillmentDetails();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-fields-expanded",
    { defaultValue: true }
  );
  const copyToClipboard = useCopyToClipboard();

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        flex: 1,
        field: "key",
        valueGetter: (_, row) => toTitleCase(row.key),
        headerName: "Question",
      },
      {
        flex: 1,
        field: "value",
        headerName: "Answer",
        renderCell: (params) => {
          if (params.row.value === null) {
            return "";
          } else {
            return (
              <span
                onClick={async () => {
                  await copyToClipboard(String(params.row.value));
                }}
                style={{ cursor: "pointer" }}
              >
                {String(params.row.value)}
              </span>
            );
          }
        },
      },
    ],
    [copyToClipboard]
  );
  const rows = data?.tierIIReport.facility.stateFields ?? [];

  return (
    <Paper sx={{ padding: theme.spacing(3), width: "100%", maxWidth: "100%" }} elevation={0}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: isExpanded ? 3 : 0,
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">State Fields{!isExpanded ? ` (${rows.length})` : ""}</Typography>
        <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={isExpanded}>
        {rows.length === 0 ? (
          <Typography
            variant="body1"
            sx={{
              color: "gray",
              padding: theme.spacing(1),
            }}
          >
            None
          </Typography>
        ) : (
          <DataGrid
            columns={columns}
            rows={rows}
            getRowId={(row) => row.key}
            pagination
            autoHeight
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: "value",
                    sort: "desc",
                  },
                  { field: "label", sort: "asc" },
                ],
              },
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 15, 50]}
            sx={{
              width: "100%",
              "& .MuiDataGrid-root": {
                width: "100%",
              },
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "200px",
              },
              "& .MuiDataGrid-columnHeaders": {
                minHeight: "auto",
              },
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordWrap: "break-word",
              },
            }}
          />
        )}
      </Collapse>
    </Paper>
  );
}
